import React from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { CardPickerModal, StatusPill, type CardOption } from '@aibuild/ui';
import type { AppSkillOption, AppSkillScope } from '@api/appBuilder';

interface AppSkillsPickerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly skills: readonly AppSkillOption[];
  readonly selected: readonly string[];
  readonly onConfirm: (selected: string[]) => void;
}

const scopeLabel = (scope: AppSkillScope, intl: IntlShape): string | null => {
  switch (scope) {
    case 'BUILT_IN':
      return null;
    case 'ORGANIZATION':
      return intl.formatMessage({ id: 'apps.builder.details.skills.scope.organization', defaultMessage: 'Organisation' });
    case 'PERSONAL':
      return intl.formatMessage({ id: 'apps.builder.details.skills.scope.personal', defaultMessage: 'Personal' });
    default: {
      const unhandled: never = scope;
      throw new Error(`Unhandled skill scope: ${String(unhandled)}`);
    }
  }
};

const toOption = (skill: AppSkillOption, intl: IntlShape): CardOption => {
  const scope = scopeLabel(skill.scope, intl);
  const localOnly = skill.hiddenOn?.includes('cloud')
    ? intl.formatMessage({ id: 'skills.form.localOnly', defaultMessage: 'Local only' })
    : null;
  const badge = [scope, localOnly].filter(Boolean).join(' · ');
  return {
    id: skill.id,
    title: skill.name,
    description: skill.description,
    badge: badge ? <StatusPill tone="neutral">{badge}</StatusPill> : undefined,
  };
};

const AppSkillsPicker: React.FC<AppSkillsPickerProps> = ({ open, onClose, skills, selected, onConfirm }) => {
  const intl = useIntl();
  const known = new Set(skills.map((skill) => skill.id));

  return (
    <CardPickerModal
      open={open}
      onClose={onClose}
      title={intl.formatMessage({ id: 'apps.builder.details.skills', defaultMessage: 'Skills' })}
      subtitle={intl.formatMessage({
        id: 'apps.builder.details.skills.subtitle',
        defaultMessage:
          "The skills this app's agent may load. Pick only what the job needs. Personal skills only work while the app is not shared.",
      })}
      options={skills.map((skill) => toOption(skill, intl))}
      selectionMode="multiple"
      initialSelected={selected.filter((id) => known.has(id))}
      onConfirm={(ids) => onConfirm(ids as string[])}
      searchable
      allowEmpty
      density="compact"
      searchPlaceholder={intl.formatMessage({ id: 'apps.builder.details.skills.search', defaultMessage: 'Search skills' })}
      confirmLabel={intl.formatMessage({ id: 'apps.builder.details.skills.apply', defaultMessage: 'Apply' })}
      cancelLabel={intl.formatMessage({ id: 'apps.builder.details.skills.cancel', defaultMessage: 'Cancel' })}
      dataTestId="app-builder__skills-picker"
    />
  );
};

export default AppSkillsPicker;
